import { useState, useEffect, useMemo, useCallback, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { api } from '@/App';
import Navbar from '@/components/Navbar';
import UserProfileModal from '@/components/UserProfileModal';

// MVP Display Component
const MVPDisplay = ({ game }) => {
  const [mvpRating, setMvpRating] = useState(null);
  
  useEffect(() => {
    const fetchMvpRating = async () => {
      if (!game.mvp_player_id) return;
      try {
        const response = await api.get(`/games/${game.id}/ratings-summary`);
        const playerAvg = response.data.player_averages[game.mvp_player_id];
        if (playerAvg) {
          setMvpRating(playerAvg.average);
        }
      } catch (error) {
        console.error('Failed to load MVP rating:', error);
      }
    };
    fetchMvpRating();
  }, [game.id, game.mvp_player_id]);
  
  if (!game.mvp_player_id) return null;
  
  const mvpPlayer = game.registered_players_info?.find(p => p.user_id === game.mvp_player_id);
  const mvpName = mvpPlayer?.name || game.mvp_name || 'Unknown';
  const mvpPhoto = mvpPlayer?.profile_photo_url;
  
  return (
    <div className="flex items-center gap-3 mb-3 p-3 bg-gradient-to-r from-yellow-50 to-amber-50 rounded-lg border-2 border-yellow-300">
      {/* MVP Profile Photo */}
      {mvpPhoto ? (
        <img 
          src={mvpPhoto} 
          alt="MVP" 
          loading="lazy"
          className="w-10 h-10 rounded-full border-2 border-yellow-500 object-cover shadow-md"
        />
      ) : (
        <div className="w-10 h-10 rounded-full border-2 border-yellow-500 bg-yellow-400 flex items-center justify-center text-yellow-900 font-bold text-lg">
          {mvpName.charAt(0)}
        </div>
      )}
      <div className="flex-1">
        <div className="flex items-center gap-2">
          <span className="text-lg">🏆</span>
          <span className="font-bold text-gray-900">MVP: {mvpName}</span>
        </div>
        {mvpRating && (
          <div className="text-sm text-gray-600">
            Rating: <span className="font-semibold text-yellow-700">{mvpRating.toFixed(2)}/10</span>
          </div>
        )}
      </div>
    </div>
  );
};

// Game Rating Display Component
const GameRatingDisplay = ({ game }) => {
  const [gameRating, setGameRating] = useState(null);
  const [ratingsCount, setRatingsCount] = useState(0);
  
  useEffect(() => {
    const fetchGameRating = async () => {
      try {
        const response = await api.get(`/games/${game.id}/ratings-summary`);
        if (response.data.game_experience_rating) {
          setGameRating(response.data.game_experience_rating);
          setRatingsCount(response.data.game_ratings_count);
        }
      } catch (error) {
        console.error('Failed to load game rating:', error);
      }
    };
    fetchGameRating();
  }, [game.id]);
  
  if (!gameRating) return null;
  
  return (
    <div className="flex items-center gap-3 mb-3 p-3 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border-2 border-green-300">
      <div className="text-2xl">⭐</div>
      <div className="flex-1">
        <div className="flex items-center gap-2">
          <span className="font-bold text-gray-900">Game Rating:</span>
          <span className="text-xl font-bold text-green-700">{gameRating}/10</span>
        </div>
        <div className="text-sm text-gray-600">
          Based on {ratingsCount} player{ratingsCount !== 1 ? 's' : ''}
        </div>
      </div>
    </div>
  );
};

// Helper function to get abbreviated position
const getPositionAbbreviation = (position) => {
  if (!position) return null;
  const abbreviations = {
    'Setter': 'S',
    'Outside Hitter': 'OH',
    'Opposite Hitter': 'OP',
    'Middle Blocker': 'MB',
    'Libero': 'L'
  };
  return abbreviations[position] || position;
};

// Helper function to get position color
const getPositionColor = (position) => {
  if (!position) return 'bg-gray-100 text-gray-600';
  
  const colors = {
    'Setter': 'bg-purple-100 text-purple-800',
    'Outside Hitter': 'bg-blue-100 text-blue-800',
    'Opposite Hitter': 'bg-green-100 text-green-800',
    'Middle Blocker': 'bg-orange-100 text-orange-800',
    'Libero': 'bg-red-100 text-red-800'
  };
  return colors[position] || 'bg-gray-100 text-gray-600';
};

// Compute the average effective rating of registered players (ignores waiting list & players with no rating)
const getTeamAverageRating = (game) => {
  const players = game?.registered_players_info || [];
  const ratings = players
    .map((p) => (typeof p.effective_rating === 'number' ? p.effective_rating : 0))
    .filter((v) => v > 0);
  if (!ratings.length) return null;
  const avg = ratings.reduce((a, b) => a + b, 0) / ratings.length;
  return { avg, count: ratings.length, total: players.length };
};

// Display player's effective rating (admin > community)
const EffectiveRatingCell = ({ player }) => {
  const source = player.rating_source || (player.rating > 0 ? 'admin' : (player.community_rating_average > 0 ? 'community' : 'none'));
  const raw =
    player.effective_rating != null
      ? player.effective_rating
      : source === 'admin'
      ? player.rating
      : source === 'community'
      ? player.community_rating_average
      : 0;
  const value = typeof raw === 'number' ? (Number.isInteger(raw) ? raw : raw.toFixed(2)) : raw || 0;
  return (
    <span className="font-bold text-purple-700" data-testid={`rating-cell-${player.user_id}`}>
      {value}
    </span>
  );
};

export default function GamesPage({ user, onLogout }) {
  const navigate = useNavigate();
  const [games, setGames] = useState([]);
  const [previousGames, setPreviousGames] = useState([]);
  const [archivedGames, setArchivedGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [whatsappMessage, setWhatsappMessage] = useState(null);
  const [showWhatsappDialog, setShowWhatsappDialog] = useState(false);
  const [profileUserId, setProfileUserId] = useState(null);

  // Add flashing animation CSS
  const flashingStyle = `
    @keyframes flash {
      0%, 50%, 100% { background-color: rgb(254 226 226); }
      25%, 75% { background-color: rgb(254 242 242); }
    }
    .flash-unpaid {
      animation: flash 2s infinite;
    }
  `;

  // Inject CSS into document
  useEffect(() => {
    const styleElement = document.createElement('style');
    styleElement.textContent = flashingStyle;
    document.head.appendChild(styleElement);
    
    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);

  useEffect(() => {
    loadGames();
    
    // Check if URL has #archived hash to auto-show archived games
    if (window.location.hash === '#archived') {
      setShowArchived(true);
      // Scroll to archived section after a short delay
      setTimeout(() => {
        const archivedSection = document.getElementById('archived-games-section');
        if (archivedSection) {
          archivedSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 300);
    }
  }, []);

  const loadGames = async () => {
    try {
      setLoading(true);
      
      // Use same approach as AdminGames - no parameters
      const response = await api.get('/games');
      
      const allGames = response.data || [];
      
      // Categorize games based on backend flags
      const categorized = {
        upcoming: [],
        previous: [],
        archived: []
      };
      
      allGames.forEach(g => {
        if (g.is_archived) {
          categorized.archived.push(g);
        } else if (g.is_previous) {
          // Use backend's is_previous field to avoid timezone issues
          categorized.previous.push(g);
        } else {
          categorized.upcoming.push(g);
        }
      });
      
      setGames(categorized.upcoming);
      setPreviousGames(categorized.previous);
      setArchivedGames(categorized.archived);
      
    } catch (error) {
      console.error('Failed to load games:', error);
      toast.error('Failed to load games');
    } finally {
      setLoading(false);
    }
  };
  
  const loadArchivedGames = async () => {
    try {
      // Fetch archived games separately when user requests them
      const response = await api.get('/games', {
        params: {
          include_archived: true
        }
      });
      
      const allGames = response.data || [];
      const archived = allGames.filter(g => g.is_archived);
      setArchivedGames(archived);
    } catch (error) {
      console.error('Failed to load archived games:', error);
      toast.error('Failed to load archived games');
    }
  };
  
  
  const isGamePast = (game) => {
    try {
      // Check if game date is in the past (compare dates only, not time)
      const [year, month, day] = game.date.split('-').map(Number);
      const gameDate = new Date(year, month - 1, day);
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Reset time to start of day
      gameDate.setHours(0, 0, 0, 0); // Reset time to start of day
      
      return gameDate < today || game.is_previous || game.is_archived;
    } catch (error) {
      console.error('Error parsing game date:', error);
      return false;
    }
  };

  const hasGameEnded = (game) => {
    try {
      // Check if game end time has passed
      const gameDateTime = new Date(`${game.date}T${game.end_time}`);
      const now = new Date();
      return gameDateTime < now;
    } catch (error) {
      console.error('Error parsing game end time:', error);
      return false;
    }
  };

  const canRateGame = (game) => {
    // Can rate if: (registered player OR admin), game ended, within 24h, hasn't been locked
    if (!user?.is_admin && !isUserRegistered(game)) return false;
    if (!hasGameEnded(game)) return false;
    if (game.ratings_locked_at) return false;
    if (game.enable_player_rating === false) return false;
    
    try {
      const gameEndTime = new Date(`${game.date}T${game.end_time}`);
      const hoursSinceEnd = (new Date() - gameEndTime) / (1000 * 60 * 60);
      return hoursSinceEnd >= 0 && hoursSinceEnd < 12; // Can rate from 0 to 12 hours after end
    } catch (error) {
      return false;
    }
  };

  const hasGameStarted = (game) => {
    try {
      // Check if game start time has passed
      const gameDateTime = new Date(`${game.date}T${game.start_time}`);
      const now = new Date();
      return gameDateTime < now;
    } catch (error) {
      console.error('Error parsing game start time:', error);
      return false;
    }
  };

  const handleRegister = async (gameId) => {
    setActionLoading(gameId);
    try {
      const response = await api.post(`/games/${gameId}/register`);
      toast.success(response.data.message);
      loadGames();
    } catch (error) {
      // Check if error is about unpaid games
      if (error.response?.data?.detail?.error === 'unpaid_games') {
        const unpaidGames = error.response.data.detail.unpaid_games;
        const unpaidList = unpaidGames.map(g => 
          `• ${g.title} on ${g.date} at ${g.venue} (€${g.fee})`
        ).join('\n');
        
        toast.error(
          `Cannot register: You have unpaid games from the last 30 days.\n\nUnpaid games:\n${unpaidList}\n\nPlease pay for previous games before registering.`,
          { duration: 8000 }
        );
      } else {
        toast.error(error.response?.data?.detail || 'Failed to register');
      }
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeregister = async (gameId) => {
    // Find the game to check conditions
    const game = games.find(g => g.id === gameId);
    if (!game) return;
    
    // Check if game has already started
    const now = new Date();
    const gameDateTime = new Date(`${game.date}T${game.start_time}`);
    const hasStarted = now > gameDateTime;
    
    if (hasStarted) {
      toast.error('Cannot cancel registration - game has already started');
      return;
    }
    
    // Calculate hours until game
    const hoursUntilGame = (gameDateTime - now) / (1000 * 60 * 60);
    const hasWaitingList = game.waiting_list && game.waiting_list.length > 0;
    
    // NEW: 12-hour hard stop - NO cancellation allowed (admins do it via admin panel)
    if (hoursUntilGame < 12) {
      toast.error('❌ Cannot cancel within 12 hours of game start. Contact admin if you have an emergency.');
      return;
    }
    
    // Between 12-24 hours: only allowed if waiting list exists
    if (hoursUntilGame < 24 && !hasWaitingList) {
      toast.error('❌ Cannot cancel within 24 hours of game start when there is no one on the waiting list');
      return;
    }
    
    setActionLoading(gameId);
    try {
      const response = await api.post(`/games/${gameId}/deregister`);
      toast.success('Successfully deregistered');
      
      // Only show WhatsApp message if user was a REGISTERED PLAYER (not waiting list)
      // and there's someone on the waiting list to be promoted
      const wasRegistered = isUserRegistered(game);
      if (wasRegistered && hasWaitingList && game.waiting_list_info && game.waiting_list_info.length > 0) {
        const nextPerson = game.waiting_list_info[0];
        const gameTitle = game.title;
        const gameDate = new Date(game.date).toLocaleDateString('en-GB', { 
          weekday: 'long', 
          day: 'numeric', 
          month: 'long' 
        });
        const gameTime = game.start_time;
        
        const message = `I can not make it for ${gameTitle} on ${gameDate} at ${gameTime}, @${nextPerson.name} you are in for this game. If you can't make it please let the next person know.`;
        
        setWhatsappMessage(message);
        setShowWhatsappDialog(true);
      }
      
      loadGames();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to deregister');
    } finally {
      setActionLoading(null);
    }
  };

  const handleCopyWhatsappMessage = () => {
    if (whatsappMessage) {
      navigator.clipboard.writeText(whatsappMessage);
      toast.success('Message copied! Paste it in your WhatsApp group');
      setShowWhatsappDialog(false);
    }
  };

  const handleMarkPayment = async (gameId) => {
    try {
      await api.post(`/games/${gameId}/mark-payment`);
      await loadGames();
      toast.success('Payment marked as complete!');
    } catch (error) {
      toast.error(typeof error.response?.data?.detail === 'string' ? error.response.data.detail : 'Failed to mark payment');
    }
  };

  const handleAddToCalendar = async (game, type) => {
    const startDateTime = `${game.date}T${game.start_time}:00`;
    const endDateTime = `${game.date}T${game.end_time}:00`;
    
    if (type === 'google') {
      // Google Calendar URL
      const googleUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(game.title || 'Volleyball Game')}&dates=${startDateTime.replace(/[-:]/g, '')}/${endDateTime.replace(/[-:]/g, '')}&details=${encodeURIComponent(`Fee: €${game.fee}\nLocation: ${game.location}`)}&location=${encodeURIComponent(game.location)}`;
      window.open(googleUrl, '_blank');
    } else if (type === 'apple') {
      try {
        // Fetch the ICS file from backend
        const response = await api.get(`/games/${game.id}/calendar`, { 
          responseType: 'blob',
          headers: { 'Accept': 'text/calendar' }
        });
        
        // Create blob URL
        const blob = new Blob([response.data], { type: 'text/calendar' });
        const url = window.URL.createObjectURL(blob);
        
        // Create temporary link and trigger download
        const link = document.createElement('a');
        link.href = url;
        link.download = `volleyball-${game.id}.ics`;
        document.body.appendChild(link);
        link.click();
        
        // Cleanup
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        // Show user-friendly message
        toast.success('Calendar file downloaded! Open it to add to your calendar.');
      } catch (error) {
        console.error('Failed to download calendar file:', error);
        toast.error('Failed to download calendar file');
      }
    }
  };

  const isUserRegistered = (game) => {
    return game.registered_players.includes(user.id);
  };

  const isUserWaiting = (game) => {
    return game.waiting_list.includes(user.id);
  };

  const getUserPaymentStatus = (game) => {
    return game.payments?.[user.id] || false;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-blue-50">
        <Navbar user={user} onLogout={onLogout} />
        <div className="flex items-center justify-center py-20">
          <div className="text-2xl font-semibold text-gray-700">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-blue-50">
      <Navbar user={user} onLogout={onLogout} />

      <div className="max-w-6xl mx-auto px-6 py-12">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Upcoming Games</h1>
          <p className="text-xl text-gray-600">Register for games and join the action!</p>
        </div>

        {games.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
            <div className="text-6xl mb-4">🏐</div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">No Games Yet</h2>
            <p className="text-gray-600">Check back soon for upcoming games!</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-6">
            {games.map((game) => (
              <div
                key={game.id}
                data-testid={`game-card-${game.id}`}
                className="bg-white rounded-2xl shadow-lg p-6 hover-lift"
              >
                {/* Game Header */}
                <div className="flex justify-between items-start mb-4">
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">{game.title || game.location}</h3>
                    
                    {/* Game Rating Display */}
                    <GameRatingDisplay game={game} />
                    
                    {/* MVP Display */}
                    <MVPDisplay game={game} />
                    
                    <a
                      href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(game.location)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline flex items-center gap-1 mb-2"
                    >
                      📍 {game.location}{game.hall ? ` - ${game.hall}` : ''}
                    </a>
                    <p className="text-gray-900 font-bold flex items-center gap-1 mb-2">
                      📅 {game.date} • {game.start_time} - {game.end_time}
                    </p>
                    {game.prioritize_by_rating && (
                      <div className="inline-block bg-purple-100 text-purple-800 text-xs font-semibold px-3 py-1 rounded-full mb-2">
                        ⭐ Players prioritized by rating
                      </div>
                    )}
                    {game.payment_link && (
                      <a
                        href={game.payment_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline font-bold text-sm flex items-center gap-2"
                      >
                        💳 Payment Link
                      </a>
                    )}
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-orange-600">€{game.fee}</div>
                  </div>
                </div>

                {/* Player Count */}
                <div className="mb-4">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-gray-600">Players</span>
                    <div className="flex items-center gap-2">
                      {(() => {
                        const avg = getTeamAverageRating(game);
                        return avg ? (
                          <span
                            data-testid={`team-avg-rating-${game.id}`}
                            className="text-xs font-semibold bg-purple-50 text-purple-700 border border-purple-200 px-2 py-0.5 rounded-full"
                            title={`Team average from ${avg.count} of ${avg.total} registered players with a rating`}
                          >
                            ⭐ Team avg: {avg.avg.toFixed(2)}
                          </span>
                        ) : null;
                      })()}
                      <span className="font-semibold">
                        {game.registered_players.length}/{game.player_limit}
                      </span>
                    </div>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div
                      className="bg-gradient-to-r from-orange-500 to-orange-600 h-3 rounded-full transition-all"
                      style={{
                        width: `${Math.min((game.registered_players.length / game.player_limit) * 100, 100)}%`
                      }}
                    ></div>
                  </div>
                </div>

                {/* Registered Players Table */}
                {game.registered_players_info && game.registered_players_info.length > 0 && (
                  <div className="mb-4 bg-green-50 rounded-lg p-3 sm:p-4">
                    <h4 className="font-semibold text-green-900 mb-3 text-sm sm:text-base">
                      ✅ Registered Players:
                    </h4>
                    <div className="overflow-x-auto -mx-3 sm:mx-0">
                      <div className="min-w-[300px]">
                        <table className="w-full text-xs sm:text-sm">
                          <thead>
                            <tr className="border-b border-green-200">
                              <th className="text-left py-2 px-1 sm:px-2 text-green-800">#</th>
                              <th className="text-left py-2 px-1 sm:px-2 text-green-800">Name</th>
                              <th className="text-center py-2 px-1 sm:px-2 text-green-800 whitespace-nowrap">Preferred Position</th>
                              {game.prioritize_by_rating && (game.registered_players.length > 0 || game.waiting_list.length > 0) && (
                                <th className="text-center py-2 px-1 sm:px-2 text-green-800">Rating</th>
                              )}
                              <th className="text-center py-2 px-1 sm:px-2 text-green-800">Payment</th>
                            </tr>
                          </thead>
                          <tbody>
                            {game.registered_players_info.map((player, index) => (
                              <tr key={player.user_id} className={`border-b border-green-100 ${!player.paid && isGamePast(game) ? 'flash-unpaid' : ''}`}>
                                <td className="py-2 px-1 sm:px-2 font-semibold text-green-700">{index + 1}</td>
                                <td className={`py-2 px-1 sm:px-2 ${!player.paid ? 'text-red-600 font-semibold' : 'text-green-900'}`}>
                                  <button
                                    type="button"
                                    data-testid={`player-link-${player.user_id}`}
                                    onClick={() => setProfileUserId(player.user_id)}
                                    className="hover:text-orange-600 text-left font-medium cursor-pointer"
                                  >
                                    {player.name}
                                  </button>
                                  {player.is_first_game && ' 🍒'}
                                  {player.is_admin && (
                                    <span className="ml-1 inline-block align-middle bg-orange-100 text-orange-700 text-[9px] font-semibold px-1 py-px rounded leading-none">A</span>
                                  )}
                                </td>
                                <td className="py-2 px-1 sm:px-2 text-center">
                                  {player.position_1st ? (
                                    <div className="flex items-center gap-1 justify-center">
                                      <span className={`${getPositionColor(player.position_1st)} px-2 py-0.5 rounded text-xs font-semibold`}>
                                        {getPositionAbbreviation(player.position_1st)}
                                      </span>
                                      {player.position_2nd && (
                                        <span className={`${getPositionColor(player.position_2nd)} px-2 py-0.5 rounded text-xs font-semibold opacity-75`}>
                                          {getPositionAbbreviation(player.position_2nd)}
                                        </span>
                                      )}
                                    </div>
                                  ) : (
                                    <span className="text-gray-400 text-xs">-</span>
                                  )}
                                </td>
                                {game.prioritize_by_rating && (game.registered_players.length > 0 || game.waiting_list.length > 0) && (
                                  <td className="py-2 px-1 sm:px-2 text-center">
                                    <EffectiveRatingCell player={player} />
                                  </td>
                                )}
                                <td className="py-2 px-1 sm:px-2 text-center">
                                  {player.paid ? (
                                    <span className="text-green-600 font-semibold text-xs sm:text-sm">✓ Paid</span>
                                  ) : (
                                    <span className="text-red-600 font-semibold text-xs sm:text-sm">✗ Unpaid</span>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                )}

                {/* Waiting List Table */}
                {game.waiting_list_info && game.waiting_list_info.length > 0 && (
                  <div className="mb-4 bg-yellow-50 rounded-lg p-3 sm:p-4">
                    <h4 className="font-semibold text-yellow-900 mb-3 text-sm sm:text-base">⏳ Waiting List:</h4>
                    <div className="overflow-x-auto -mx-3 sm:mx-0">
                      <div className="min-w-[250px]">
                        <table className="w-full text-xs sm:text-sm">
                          <thead>
                            <tr className="border-b border-yellow-200">
                              <th className="text-left py-2 px-1 sm:px-2 text-yellow-800">#</th>
                              <th className="text-left py-2 px-1 sm:px-2 text-yellow-800">Name</th>
                              <th className="text-center py-2 px-1 sm:px-2 text-yellow-800 whitespace-nowrap">Preferred Position</th>
                              {game.prioritize_by_rating && (game.registered_players.length > 0 || game.waiting_list.length > 0) && (
                                <th className="text-center py-2 px-1 sm:px-2 text-yellow-800">Rating</th>
                              )}
                            </tr>
                          </thead>
                          <tbody>
                            {game.waiting_list_info.map((player, index) => (
                              <tr key={player.user_id} className="border-b border-yellow-100">
                                <td className="py-2 px-1 sm:px-2 font-semibold text-yellow-700">{index + 1}</td>
                                <td className="py-2 px-1 sm:px-2 text-yellow-900">
                                  <button
                                    type="button"
                                    data-testid={`player-link-${player.user_id}`}
                                    onClick={() => setProfileUserId(player.user_id)}
                                    className="hover:text-orange-600 text-left font-medium cursor-pointer"
                                  >
                                    {player.name}
                                  </button>
                                  {player.is_first_game && ' 🍒'}
                                  {player.is_admin && (
                                    <span className="ml-1 inline-block align-middle bg-orange-100 text-orange-700 text-[9px] font-semibold px-1 py-px rounded leading-none">A</span>
                                  )}
                                </td>
                                <td className="py-2 px-1 sm:px-2 text-center">
                                  {player.position_1st ? (
                                    <div className="flex items-center gap-1 justify-center">
                                      <span className={`${getPositionColor(player.position_1st)} px-2 py-0.5 rounded text-xs font-semibold`}>
                                        {getPositionAbbreviation(player.position_1st)}
                                      </span>
                                      {player.position_2nd && (
                                        <span className={`${getPositionColor(player.position_2nd)} px-2 py-0.5 rounded text-xs font-semibold opacity-75`}>
                                          {getPositionAbbreviation(player.position_2nd)}
                                        </span>
                                      )}
                                    </div>
                                  ) : (
                                    <span className="text-gray-400 text-xs">-</span>
                                  )}
                                </td>
                                {game.prioritize_by_rating && (game.registered_players.length > 0 || game.waiting_list.length > 0) && (
                                  <td className="py-2 px-1 sm:px-2 text-center">
                                    <EffectiveRatingCell player={player} />
                                  </td>
                                )}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex flex-wrap gap-2">
                  {/* Admin Rate Players — visible even if admin didn't participate */}
                  {user?.is_admin && !isUserRegistered(game) && canRateGame(game) && (
                    <Button
                      data-testid={`admin-rate-players-btn-${game.id}`}
                      onClick={() => navigate(`/games/${game.id}/rate`)}
                      size="sm"
                      className="bg-purple-600 hover:bg-purple-700 text-white"
                    >
                      ⭐ Rate Players (Admin)
                    </Button>
                  )}
                  {isUserRegistered(game) ? (
                    <>
                      <Badge
                        data-testid={`registration-badge-${game.id}`}
                        className="bg-green-100 text-green-700 border-green-300"
                      >
                        Registered {getUserPaymentStatus(game) && '• Paid'}
                      </Badge>
                      
                      {!getUserPaymentStatus(game) && (
                        <Button
                          data-testid={`mark-paid-btn-${game.id}`}
                          onClick={() => handleMarkPayment(game.id)}
                          size="sm"
                          className="bg-green-600 hover:bg-green-700 text-white"
                        >
                          Mark as Paid
                        </Button>
                      )}
                      
                      {/* Rate Players Button */}
                      {canRateGame(game) && (
                        <Button
                          onClick={() => navigate(`/games/${game.id}/rate`)}
                          size="sm"
                          className="bg-purple-600 hover:bg-purple-700 text-white"
                        >
                          ⭐ Rate Players
                        </Button>
                      )}
                      
                      {/* MVP Badge */}
                      {game.mvp_player_id === user.id && (
                        <Badge className="bg-yellow-100 text-yellow-700 border-2 border-yellow-400 font-bold">
                          🏆 MVP
                        </Badge>
                      )}
                      
                      {!isGamePast(game) && !hasGameStarted(game) && (
                        <Button
                          data-testid={`deregister-btn-${game.id}`}
                          onClick={() => handleDeregister(game.id)}
                          disabled={actionLoading === game.id}
                          variant="outline"
                          size="sm"
                          className="border-red-500 text-red-600 hover:bg-red-50"
                        >
                          {actionLoading === game.id ? 'Processing...' : 'Cancel'}
                        </Button>
                      )}
                      
                      {!hasGameEnded(game) && (
                        <div className="flex gap-1">
                          <Button
                            onClick={() => handleAddToCalendar(game, 'google')}
                            size="sm"
                            variant="outline"
                            className="border-blue-500 text-blue-600 hover:bg-blue-50"
                          >
                            📅 Google
                          </Button>
                          <Button
                            onClick={() => handleAddToCalendar(game, 'apple')}
                            size="sm"
                            variant="outline"
                            className="border-blue-500 text-blue-600 hover:bg-blue-50"
                          >
                            📅 Apple
                          </Button>
                        </div>
                      )}
                    </>
                  ) : isUserWaiting(game) ? (
                    <>
                      <Badge className="bg-yellow-100 text-yellow-700 border-yellow-300">On Waiting List</Badge>
                      {!isGamePast(game) && !hasGameStarted(game) && (
                        <Button
                          data-testid={`deregister-btn-${game.id}`}
                          onClick={() => handleDeregister(game.id)}
                          disabled={actionLoading === game.id}
                          variant="outline"
                          className="ml-auto border-red-500 text-red-600 hover:bg-red-50"
                        >
                          {actionLoading === game.id ? 'Processing...' : 'Leave Waiting List'}
                        </Button>
                      )}
                    </>
                  ) : (
                    !isGamePast(game) && !hasGameEnded(game) && (
                      <Button
                        data-testid={`register-btn-${game.id}`}
                        onClick={() => handleRegister(game.id)}
                        disabled={actionLoading === game.id}
                        className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-semibold"
                      >
                        {actionLoading === game.id ? 'Processing...' : 'Register'}
                      </Button>
                    )
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Archived Games Section */}
        {archivedGames.length > 0 && (
          <div id="archived-games-section" className="mt-12">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-3xl font-bold text-gray-800">📦 Archived Games</h2>
              <Button
                onClick={() => {
                  const newShowArchived = !showArchived;
                  setShowArchived(newShowArchived);
                  // Load archived games when showing them
                  if (newShowArchived && archivedGames.length === 0) {
                    loadArchivedGames();
                  }
                }}
                variant="outline"
                className="border-gray-400 text-gray-700"
              >
                {showArchived ? 'Hide' : 'Show'} ({archivedGames.length})
              </Button>
            </div>

            {showArchived && (
              <div className="grid md:grid-cols-2 gap-6">
                {archivedGames.map((game) => (
                  <div
                    key={game.id}
                    data-testid={`archived-game-card-${game.id}`}
                    className="bg-white rounded-2xl shadow-lg p-6 hover-lift"
                  >
                    {/* Game Header - Same as upcoming games */}
                    <div className="flex justify-between items-start mb-4">
                      <div className="flex-1">
                        <h3 className="text-2xl font-bold text-gray-900 mb-2">{game.title || game.location}</h3>
                        
                        {/* Game Rating Display */}
                        <GameRatingDisplay game={game} />
                        
                        {/* MVP Display */}
                        <MVPDisplay game={game} />
                        
                        <a
                          href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(game.location)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:underline flex items-center gap-1 mb-2"
                        >
                          📍 {game.location}{game.hall ? ` - ${game.hall}` : ''}
                        </a>
                        <p className="text-gray-900 font-bold mb-2 flex items-center gap-1">
                          📅 {game.date} • {game.start_time} - {game.end_time}
                        </p>
                        {game.payment_link && (
                          <a
                            href={game.payment_link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:underline font-bold text-sm flex items-center gap-2 mb-3"
                          >
                            💳 Payment Link
                          </a>
                        )}
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-bold text-orange-600">€{game.fee}</div>
                        <Badge className="mt-2 bg-gray-500 text-white">Archived</Badge>
                      </div>
                    </div>

                    {/* Stats */}
                    <div className="flex gap-4 mb-4">
                      <div className="flex-1 bg-orange-50 rounded-lg p-3">
                        <div className="flex justify-between items-start mb-1">
                          <p className="text-sm text-gray-600">Players</p>
                          {(() => {
                            const avg = getTeamAverageRating(game);
                            return avg ? (
                              <span
                                data-testid={`team-avg-rating-${game.id}`}
                                className="text-xs font-semibold bg-purple-50 text-purple-700 border border-purple-200 px-2 py-0.5 rounded-full"
                                title={`Team average from ${avg.count} of ${avg.total} registered players with a rating`}
                              >
                                ⭐ {avg.avg.toFixed(2)}
                              </span>
                            ) : null;
                          })()}
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-2xl font-bold text-gray-900">
                            {game.registered_players.length}/{game.player_limit}
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                          <div 
                            className="bg-gradient-to-r from-orange-500 to-orange-600 h-2 rounded-full transition-all"
                            style={{
                              width: `${Math.min((game.registered_players.length / game.player_limit) * 100, 100)}%`
                            }}
                          ></div>
                        </div>
                      </div>
                    </div>

                    {/* Registered Players Table */}
                    {game.registered_players_info && game.registered_players_info.length > 0 && (
                      <div className="mb-4 bg-green-50 rounded-lg p-3 sm:p-4">
                        <h4 className="font-semibold text-green-900 mb-3 text-sm sm:text-base">
                          ✅ Registered Players:
                        </h4>
                        <div className="overflow-x-auto -mx-3 sm:mx-0">
                          <div className="min-w-[300px]">
                            <table className="w-full text-xs sm:text-sm">
                              <thead>
                                <tr className="border-b border-green-200">
                                  <th className="text-left py-2 px-1 sm:px-2 text-green-800">#</th>
                                  <th className="text-left py-2 px-1 sm:px-2 text-green-800">Name</th>
                                  <th className="text-center py-2 px-1 sm:px-2 text-green-800 whitespace-nowrap">Preferred Position</th>
                                  {game.prioritize_by_rating && (game.registered_players.length > 0 || game.waiting_list.length > 0) && (
                                    <th className="text-center py-2 px-1 sm:px-2 text-green-800">Rating</th>
                                  )}
                                  <th className="text-center py-2 px-1 sm:px-2 text-green-800">Payment</th>
                                </tr>
                              </thead>
                              <tbody>
                                {game.registered_players_info.map((player, index) => (
                              <tr key={player.user_id} className={`border-b border-green-100 ${!player.paid && isGamePast(game) ? 'flash-unpaid' : ''}`}>
                                <td className="py-2 px-1 sm:px-2 font-semibold text-green-700">{index + 1}</td>
                                <td className={`py-2 px-1 sm:px-2 ${!player.paid ? 'text-red-600 font-semibold' : 'text-green-900'}`}>
                                  <button
                                    type="button"
                                    data-testid={`player-link-${player.user_id}`}
                                    onClick={() => setProfileUserId(player.user_id)}
                                    className="hover:text-orange-600 text-left font-medium cursor-pointer"
                                  >
                                    {player.name}
                                  </button>
                                  {player.is_first_game && ' 🍒'}
                                  {player.is_admin && (
                                    <span className="ml-1 inline-block align-middle bg-orange-100 text-orange-700 text-[9px] font-semibold px-1 py-px rounded leading-none">A</span>
                                  )}
                                </td>
                                    <td className="py-2 px-1 sm:px-2 text-center">
                                      {player.position_1st ? (
                                        <div className="flex items-center gap-1 justify-center">
                                          <span className={`${getPositionColor(player.position_1st)} px-2 py-0.5 rounded text-xs font-semibold`}>
                                            {getPositionAbbreviation(player.position_1st)}
                                          </span>
                                          {player.position_2nd && (
                                            <span className={`${getPositionColor(player.position_2nd)} px-2 py-0.5 rounded text-xs font-semibold opacity-75`}>
                                              {getPositionAbbreviation(player.position_2nd)}
                                            </span>
                                          )}
                                        </div>
                                      ) : (
                                        <span className="text-gray-400 text-xs">-</span>
                                      )}
                                    </td>
                                    {game.prioritize_by_rating && (game.registered_players.length > 0 || game.waiting_list.length > 0) && (
                                      <td className="py-2 px-1 sm:px-2 text-center">
                                        <EffectiveRatingCell player={player} />
                                      </td>
                                    )}
                                    <td className="py-2 px-1 sm:px-2 text-center">
                                      {player.paid ? (
                                        <span className="text-green-600 font-semibold text-xs sm:text-sm">✓ Paid</span>
                                      ) : (
                                        <span className="text-red-600 font-semibold text-xs sm:text-sm">✗ Unpaid</span>
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Waiting List Table */}
                    {game.waiting_list_info && game.waiting_list_info.length > 0 && (
                      <div className="mb-4 bg-yellow-50 rounded-lg p-3 sm:p-4">
                        <h4 className="font-semibold text-yellow-900 mb-3 text-sm sm:text-base">⏳ Waiting List:</h4>
                        <div className="overflow-x-auto -mx-3 sm:mx-0">
                          <div className="min-w-[300px]">
                            <table className="w-full text-xs sm:text-sm">
                              <thead>
                                <tr className="border-b border-yellow-200">
                                  <th className="text-left py-2 px-1 sm:px-2 text-yellow-800">#</th>
                                  <th className="text-left py-2 px-1 sm:px-2 text-yellow-800">Name</th>
                                  <th className="text-center py-2 px-1 sm:px-2 text-yellow-800 whitespace-nowrap">Preferred Position</th>
                                  {game.prioritize_by_rating && (game.registered_players.length > 0 || game.waiting_list.length > 0) && (
                                    <th className="text-center py-2 px-1 sm:px-2 text-yellow-800">Rating</th>
                                  )}
                                </tr>
                              </thead>
                              <tbody>
                                {game.waiting_list_info.map((player, index) => (
                                  <tr key={player.user_id} className="border-b border-yellow-100">
                                    <td className="py-2 px-1 sm:px-2 font-semibold text-yellow-700">{index + 1}</td>
                                    <td className="py-2 px-1 sm:px-2 text-yellow-900">
                                  <button
                                    type="button"
                                    data-testid={`player-link-${player.user_id}`}
                                    onClick={() => setProfileUserId(player.user_id)}
                                    className="hover:text-orange-600 text-left font-medium cursor-pointer"
                                  >
                                    {player.name}
                                  </button>
                                  {player.is_first_game && ' 🍒'}
                                  {player.is_admin && (
                                    <span className="ml-1 inline-block align-middle bg-orange-100 text-orange-700 text-[9px] font-semibold px-1 py-px rounded leading-none">A</span>
                                  )}
                                </td>
                                    <td className="py-2 px-1 sm:px-2 text-center">
                                      {player.position_1st ? (
                                        <div className="flex items-center gap-1 justify-center">
                                          <span className={`${getPositionColor(player.position_1st)} px-2 py-0.5 rounded text-xs font-semibold`}>
                                            {getPositionAbbreviation(player.position_1st)}
                                          </span>
                                          {player.position_2nd && (
                                            <span className={`${getPositionColor(player.position_2nd)} px-2 py-0.5 rounded text-xs font-semibold opacity-75`}>
                                              {getPositionAbbreviation(player.position_2nd)}
                                            </span>
                                          )}
                                        </div>
                                      ) : (
                                        <span className="text-gray-400 text-xs">-</span>
                                      )}
                                    </td>
                                    {game.prioritize_by_rating && (game.registered_players.length > 0 || game.waiting_list.length > 0) && (
                                      <td className="py-2 px-1 sm:px-2 text-center">
                                        <EffectiveRatingCell player={player} />
                                      </td>
                                    )}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Action Buttons - Same as upcoming games */}
                    <div className="flex flex-wrap gap-2">
                      {/* Admin Rate Players — visible even if admin didn't participate */}
                      {user?.is_admin && !isUserRegistered(game) && canRateGame(game) && (
                        <Button
                          data-testid={`admin-rate-players-btn-${game.id}`}
                          onClick={() => navigate(`/games/${game.id}/rate`)}
                          size="sm"
                          className="bg-purple-600 hover:bg-purple-700 text-white"
                        >
                          ⭐ Rate Players (Admin)
                        </Button>
                      )}
                      {isUserRegistered(game) ? (
                        <>
                          <Badge className="bg-green-100 text-green-700 border-green-300">
                            Registered {getUserPaymentStatus(game) && '• Paid'}
                          </Badge>
                          
                          {!getUserPaymentStatus(game) && (
                            <Button
                              onClick={() => handleMarkPayment(game.id)}
                              size="sm"
                              className="bg-green-600 hover:bg-green-700 text-white"
                            >
                              Mark as Paid
                            </Button>
                          )}

                          {/* Rate Players Button (for registered players including admin) */}
                          {canRateGame(game) && (
                            <Button
                              onClick={() => navigate(`/games/${game.id}/rate`)}
                              size="sm"
                              className="bg-purple-600 hover:bg-purple-700 text-white"
                            >
                              ⭐ Rate Players
                            </Button>
                          )}
                          
                          <div className="flex gap-1">
                            <Button
                              onClick={() => handleAddToCalendar(game, 'google')}
                              size="sm"
                              variant="outline"
                              className="border-blue-500 text-blue-600 hover:bg-blue-50"
                            >
                              📅 Google
                            </Button>
                            <Button
                              onClick={() => handleAddToCalendar(game, 'apple')}
                              size="sm"
                              variant="outline"
                              className="border-blue-500 text-blue-600 hover:bg-blue-50"
                            >
                              📅 Apple
                            </Button>
                          </div>
                        </>
                      ) : isUserWaiting(game) ? (
                        <>
                          <Badge className="bg-yellow-100 text-yellow-700 border-yellow-300">On Waiting List</Badge>
                        </>
                      ) : (
                        <Badge className="bg-gray-100 text-gray-700 border-gray-300">Not Registered</Badge>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* WhatsApp Message Copy Dialog */}
      <Dialog open={showWhatsappDialog} onOpenChange={setShowWhatsappDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>📱 Notify Next Person on WhatsApp</DialogTitle>
            <DialogDescription>
              Copy this message and share it in your WhatsApp group to notify the next person on the waiting list.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
              <p className="text-sm text-gray-700 whitespace-pre-wrap">{whatsappMessage}</p>
            </div>
            
            <div className="flex gap-2">
              <Button 
                onClick={handleCopyWhatsappMessage}
                className="flex-1 bg-green-600 hover:bg-green-700"
              >
                📋 Copy Message
              </Button>
              <Button 
                onClick={() => setShowWhatsappDialog(false)}
                variant="outline"
              >
                Close
              </Button>
            </div>
            
            <p className="text-xs text-gray-500 text-center">
              💡 Tip: Paste this in your WhatsApp group and tag the person using @mention
            </p>
          </div>
        </DialogContent>
      </Dialog>

      <UserProfileModal
        userId={profileUserId}
        open={!!profileUserId}
        onClose={() => setProfileUserId(null)}
      />
    </div>
  );
}
